import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import {
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  Box,
  MenuItem,
  Autocomplete,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import type { FilterOptionsState } from "@mui/material/useAutocomplete";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getCurrentUserDetails, updateSessionDetails } from "../../../../common/routines/user-operations";
import ActionConfirmationPopup from "../../../../common/components/action-confirmation-popup/action-confirmation-popup";

import { ReactComponent as PhotoIcon } from "../../../../assets/icons/photo-icon.svg"

import { userProfileValidationSchema } from "../validationSchema";

//  same address helpers
import { addressData, extractAllOptions, filterOptionsByValue } from "../../../../utils/extractCityStateZipCode";

import "./preneed-profile-form.scss";
import { useDispatch } from "react-redux";
import { deletePreneedPhoto, updatePreneedProfile, createPreneedProfile, uploadPreneedPhoto } from "../../../../api/preneed-section/preneed-profile.api";

type Option = { value: string; label: string };

type Address = {
  street: string;
  city: string;
  state: string;
  country: string;
  zipcode: string;
};

type PreneedProfileFormProps = {
  isProfileCreated: boolean;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  preneedProfileDetails: any;
  setPreneedProfileDetails?: (val: any) => void;
  fetchPreneedProfileDetails: () => Promise<void>;
  onChangePassword?: () => void; // optional - for security block
  onCancel?: () => void;
  onSave?: () => void;
};

const PreneedProfileForm: React.FC<PreneedProfileFormProps> = ({
  isProfileCreated,
  setIsEditMode,
  preneedProfileDetails,
  setPreneedProfileDetails,
  fetchPreneedProfileDetails,
  onChangePassword,
}) => {
  const dispatch = useDispatch();

  // ---------------------------
  // PHOTO STATES (same as UserProfileForm)
  // ---------------------------

  const token = getCurrentUserDetails("preNeed")?.token;


  const normalizedProfile = useMemo(() => {
    const d = preneedProfileDetails || {};

    return {
      fullName: d.fullName ?? "",
      preneedId: d.preneedId ?? d.preneed_id ?? d.providerId ?? "",
      email: d.businessEmail ?? d.email ?? "",
      phoneNumber: d.phoneNumber ?? "",
      profileImage: d.profileImage ?? d.profile_photo ?? d.profilePhoto ?? null,
      address: d.address ?? {
        street: "",
        city: "",
        state: "",
        country: "",
        zipcode: "",
      },
    };
  }, [preneedProfileDetails]);
  const [preview, setPreview] = useState<string | null>(normalizedProfile.profileImage);

  const [uploading, setUploading] = useState(false);

  // ---------------------------
  // snackbar (same)
  // ---------------------------
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("warning");

  const [isDeletePhotoPopupOpen, setIsDeletePhotoPopupOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleImageUpload = async (file: File) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("providerProfilePhoto", file);

      const uploadRes = await uploadPreneedPhoto(formData);

      if (uploadRes?.data?.success) {
        const uploadedPhotoUrl =
          uploadRes?.data?.data?.profile_photo ??
          uploadRes?.data?.data?.profilePhoto ??
          uploadRes?.data?.data?.url ??
          uploadRes?.data?.data?.providerProfilePhoto ??
          uploadRes?.data?.data ??
          null;

        updateSessionDetails("preNeed", { profile_photo: uploadedPhotoUrl });

        setPreview(uploadedPhotoUrl);
        formik.setFieldValue("profile_photo", file);
        formik.setFieldValue("profileImageRemoved", false);
        setSnackbarSeverity("success");
        setSnackbarMessage("Profile picture updated successfully");
        setSnackbarOpen(true);

        await fetchPreneedProfileDetails();
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      console.error("Photo upload failed", err);
      setSnackbarSeverity("error");
      setSnackbarMessage(getApiErrorMessage(err));
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
    }
  };

  // ---------------------------
  // Autocomplete helpers (same)
  // ---------------------------
  const allAddressOptions = extractAllOptions(addressData);

  const defaultFilter = createFilterOptions<Option>({
    matchFrom: "any",
    stringify: (o) => `${o.label} ${o.value}`,
  });

  const limitFilterOptions = (
    options: Option[],
    state: FilterOptionsState<Option>
  ) => {
    const hasQuery = (state.inputValue || "").trim().length > 0;
    const filtered = hasQuery ? defaultFilter(options, state) : options;
    return filtered.slice(0, hasQuery ? 50 : 10);
  };

  const getApiErrorMessage = (err: any) => {
    // Most common patterns
    return (
      err?.response?.data?.message ||
      err?.response?.data?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      "Something went wrong. Please try again."
    );
  };

  // ---------------------------
  // initial values (close to UserProfileForm)
  // ---------------------------
  const initialValues = useMemo(
    () => ({
      fullName: normalizedProfile.fullName,
      email: normalizedProfile.email,
      phoneNumber: normalizedProfile.phoneNumber,
      preneedId: normalizedProfile.preneedId,

      address: normalizedProfile.address,

      profile_photo: null as File | null,
      profileImageRemoved: false,
    }),
    [normalizedProfile]
  );


  // lock fields (same style)
  const disableLockedFields = {
    fullName: false,
    phoneNumber: false,
    email: true,
    preneedId: true,
  };


  // ---------------------------
  // formik (same style)
  // ---------------------------
  const formik = useFormik({
    initialValues,
    validationSchema: userProfileValidationSchema,
    validateOnMount: true,
    enableReinitialize: true,

    onSubmit: async (values) => {
      if (!formik.dirty) {
        setIsEditMode(false);
        return;
      }

      // Photo is now uploaded immediately upon selection
      let uploadedPhotoUrl: string | null = preview;

      if (values.profileImageRemoved) {
        uploadedPhotoUrl = null;
      }

      //  payload (close to your buildUserProfilePayload)
      const payload = buildPreneedProfilePayload({
        values,
        uploadedPhotoUrl,
      });

      try {
        let response;

        if (isProfileCreated) {
          response = await updatePreneedProfile(payload);
        } else {
          response = await createPreneedProfile(payload);
        }

        // Optional: email uniqueness check like yours
        if (
          response?.data?.data?.message ===
          "The Email Address entered is already in use."
        ) {
          setSnackbarMessage(response.data?.data?.message);
          setSnackbarOpen(true);
          return;
        }

        // update redux basic data
        const nameParts = payload.fullName.trim().split(/\s+/);
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        updateSessionDetails("preNeed", {
          fullName: payload.fullName,
          first_name: firstName,
          last_name: lastName,
          email: payload.businessEmail, // API field
          phoneNumber: payload.phoneNumber,
          profile_photo: uploadedPhotoUrl ?? normalizedProfile.profileImage,
        });


        await fetchPreneedProfileDetails();
        setSnackbarSeverity("success");
        setSnackbarMessage(isProfileCreated ? "Profile updated successfully." : "Profile created successfully.");
        setSnackbarOpen(true);
        setIsEditMode(false);
      } catch (error) {
        console.error("Preneed profile update failed", error);
        setSnackbarSeverity("error");
        setSnackbarMessage(getApiErrorMessage(error));
        setSnackbarOpen(true);
      }
    },
  });

  // ---------------------------
  // derived options (same pattern as permanent address)
  // ---------------------------
  const cityOptions = useMemo<Option[]>(() => {
    const state = formik.values.address.state;
    const zipcode = formik.values.address.zipcode;

    if (state && zipcode) {
      return filterOptionsByValue(addressData, "State", state, "Code", zipcode)
        .cityOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    if (state) {
      return filterOptionsByValue(addressData, "State", state).cityOptions.sort(
        (a, b) => a.label.localeCompare(b.label)
      );
    }
    if (zipcode) {
      return filterOptionsByValue(addressData, "Code", zipcode).cityOptions.sort(
        (a, b) => a.label.localeCompare(b.label)
      );
    }
    return allAddressOptions.cityOptions.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [formik.values.address.state, formik.values.address.zipcode]);

  const zipOptions = useMemo<Option[]>(() => {
    const city = formik.values.address.city;
    const state = formik.values.address.state;

    if (state && city) {
      return filterOptionsByValue(addressData, "City", city, "State", state)
        .postCodeOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    if (state) {
      return filterOptionsByValue(addressData, "State", state).postCodeOptions.sort(
        (a, b) => a.label.localeCompare(b.label)
      );
    }
    if (city) {
      return filterOptionsByValue(addressData, "City", city).postCodeOptions.sort(
        (a, b) => a.label.localeCompare(b.label)
      );
    }
    return allAddressOptions.postCodeOptions.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [formik.values.address.city, formik.values.address.state]);

  const stateOptions = useMemo<Option[]>(() => {
    const city = formik.values.address.city;
    const zipCode = formik.values.address.zipcode;

    if (city && zipCode) {
      return filterOptionsByValue(addressData, "City", city, "Code", zipCode)
        .stateOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    if (city) {
      return filterOptionsByValue(addressData, "City", city).stateOptions.sort(
        (a, b) => a.label.localeCompare(b.label)
      );
    }
    if (zipCode) {
      return filterOptionsByValue(addressData, "Code", zipCode).stateOptions.sort(
        (a, b) => a.label.localeCompare(b.label)
      );
    }
    return allAddressOptions.stateOptions.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [formik.values.address.city, formik.values.address.zipcode]);

  const cityByValue = useMemo(() => new Map(cityOptions.map((o) => [o.value, o])), [
    cityOptions,
  ]);
  const zipByValue = useMemo(() => new Map(zipOptions.map((o) => [o.value, o])), [
    zipOptions,
  ]);
  const stateByValue = useMemo(
    () => new Map(stateOptions.map((o) => [o.value, o])),
    [stateOptions]
  );

  // ---------------------------
  // address handlers (same style)
  // ---------------------------
  const handleCityChange = async (_: any, newValue: Option | null) => {
    if (newValue) {
      const { stateOptions: sOpts, postCodeOptions } = filterOptionsByValue(
        addressData,
        "City",
        newValue.value
      );

      await formik.setFieldValue("address.city", newValue.value, false);
      formik.setFieldTouched("address.city", true, true);

      if (sOpts.length === 1) {
        formik.setFieldValue("address.state", sOpts[0].value, true);
      } else {
        formik.setFieldValue("address.state", "", true);
      }

      if (postCodeOptions.length === 1) {
        await formik.setFieldValue("address.zipcode", postCodeOptions[0].value, true);
        formik.setFieldTouched("address.zipcode", true);
      } else {
        await formik.setFieldValue("address.zipcode", "", false);
        formik.setFieldTouched("address.zipcode", true);
      }
    } else {
      await formik.setFieldValue("address.city", "", false);
      await formik.setFieldValue("address.zipcode", "", false);
      formik.setFieldTouched("address.city", true);
      formik.setFieldTouched("address.zipcode", true);
    }

    formik.setFieldTouched("address.city", true, true);
    await formik.validateForm();
  };

  const handleZipcodeChange = async (_: any, newValue: Option | null) => {
    if (newValue) {
      const { cityOptions: cOpts, stateOptions: sOpts } = filterOptionsByValue(
        addressData,
        "Code",
        newValue.value
      );

      await formik.setFieldValue("address.zipcode", newValue.value, false);
      formik.setFieldTouched("address.zipcode", true);

      if (cOpts.length === 1) {
        await formik.setFieldValue("address.city", cOpts[0].value, false);
      } else {
        formik.setFieldValue("address.city", "", true);
      }

      if (sOpts.length === 1) {
        await formik.setFieldValue("address.state", sOpts[0].value, false);
      } else {
        formik.setFieldValue("address.state", "", true);
        formik.setFieldTouched("address.city", true);
      }
    } else {
      await formik.setFieldValue("address.zipcode", "", false);
      formik.setFieldTouched("address.zipcode", true);
    }

    formik.setFieldTouched("address.zipcode", true, true);
    await formik.validateForm();
  };

  const handleStateChange = async (_: any, newValue: Option | null) => {
    if (newValue) {
      const { postCodeOptions } = filterOptionsByValue(
        addressData,
        "State",
        newValue.value
      );

      formik.setFieldValue("address.state", newValue.value, true);
      formik.setFieldTouched("address.state", true, true);

      if (postCodeOptions.length === 1) {
        formik.setFieldValue("address.zipcode", postCodeOptions[0].value, true);
      } else {
        formik.setFieldValue("address.zipcode", "", true);
      }
    } else {
      await formik.setFieldValue("address.state", "", false);
      await formik.setFieldValue("address.city", "", false);
      await formik.setFieldValue("address.zipcode", "", false);
      formik.setFieldTouched("address.state", true);
      formik.setFieldTouched("address.city", true);
      formik.setFieldTouched("address.zipcode", true);
    }

    formik.setFieldTouched("address.state", true, true);
    await formik.validateForm();
  };

  const handleAlphaChange = (e: any) => {
    const { name, value } = e.target;
    const filtered = value.replace(/[^A-Za-z ]/g, "").replace(/\s+/g, " ");
    formik.setFieldValue(name, filtered);
    formik.setFieldTouched(name, true, true);
  };

  const handleDigitChange = (e: any) => {
    const { name, value } = e.target;
    const filtered = value.replace(/\D/g, "");
    const maxLength = 10;
    formik.setFieldValue(name, filtered.slice(0, maxLength));
    formik.setFieldTouched(name, true, true);
  };

  const handleAddressChange = (e: any) => {
    const { name, value } = e.target;
    formik.setFieldValue(`address.${name}`, value);
    formik.setFieldTouched(`address.${name}`, true, true);
  };

  const handleCancel = () => setIsEditMode((prev) => !prev);

  // ---------------------------
  // photo delete (same)
  // ---------------------------
  const handleDeletePhoto = async () => {
    try {
      setIsDeletePhotoPopupOpen(false);
      const res = await deletePreneedPhoto();

      if (res?.data?.success) {
        updateSessionDetails("preNeed", { profile_photo: null });

        setPreview(null);
        formik.setFieldValue("profile_photo", null);
        formik.setFieldValue("profileImageRemoved", true);
        if (fileInputRef.current) fileInputRef.current.value = "";

        await fetchPreneedProfileDetails();
      }
    } catch (error) {
      console.error("ERROR DELETE PHOTO::", error);
    }
  };

  useEffect(() => {
    setPreview(normalizedProfile.profileImage);
  }, [normalizedProfile.profileImage]);



  // ---------------------------
  // payload builder (close to yours)
  // ---------------------------
  const buildPreneedProfilePayload = ({
    values,
    uploadedPhotoUrl,
  }: any) => {
    const normalizedAddress = {
      street: values.address?.street?.trim() || "",
      city: values.address?.city?.trim() || "",
      state: values.address?.state?.trim() || "",
      country: values.address?.country?.trim() || "",
      zipcode: values.address?.zipcode?.trim() || "",
      address: normalizedProfile,
    };
    const payload: any = {
      fullName: values.fullName,
      businessEmail: normalizedProfile.email, // keep locked
      phoneNumber: values.phoneNumber,
      preneedId: normalizedProfile.preneedId, // keep locked
      address: { ...values.address },
    };

    // if (uploadedPhotoUrl) {
    //   payload.profile_photo = uploadedPhotoUrl;
    // }

    if (uploadedPhotoUrl !== undefined) {
      payload.profileImage = uploadedPhotoUrl;
    }

    payload.address = { ...values.address };

    return payload;
  };

  // ---------------------------
  // validations for submit (similar)
  // ---------------------------
  const canSubmit = formik.isValid && formik.dirty;
  const renderLabel = (text: string, required = false) => (
    <Typography className="preneed-profile-form__label">
      {text}
      {required && <span className="preneed-profile-form__asterisk">*</span>}
    </Typography>
  );

  // =========================================================
  // EDIT MODE (Formik form, close to UserProfileForm)
  // =========================================================
  return (
    <Paper className="preneed-profile-form">
      <form onSubmit={formik.handleSubmit}>
        {/* 1. Profile Photo + Basic Info */}
        <div className="preneed-profile-form__section">
          <Typography variant="h6" className="preneed-profile-form__heading-profile">
            Edit Profile
          </Typography>

          <div className="preneed-profile-form__photo">
            <Typography className="preneed-profile-form__profile-text">
              Profile Photo (Optional)
            </Typography>

            <input
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.svg,image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;

                const allowedTypes = [
                  "image/jpeg",
                  "image/jpg",
                  "image/png",
                  "image/webp",
                  "image/svg+xml",
                ];

                if (!allowedTypes.includes(file.type)) {
                  alert(
                    "Invalid file type. Only .jpg, .jpeg, .png, .webp, .svg files are allowed."
                  );
                  e.target.value = "";
                  return;
                }

                const maxSizeMB = 10;
                if (file.size / 1024 / 1024 > maxSizeMB) {
                  alert(`File is too large. Maximum allowed size is ${maxSizeMB} MB.`);
                  e.target.value = "";
                  return;
                }

                handleImageUpload(file);
              }}
            />

            <Box
              className="preneed-profile-form__photo-left"
              onClick={() => fileInputRef.current?.click()}
              style={{ cursor: "pointer" }}
            >
              <Box sx={{ width: 95, height: 95, position: "relative" }}>
                <Box
                  className="preneed-profile-form__photo-icon"
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f0f0f0",
                  }}
                >
                  {uploading ? (
                    <CircularProgress />
                  ) : preview ? (
                    <img
                      src={preview}
                      alt="Preview"
                      className="preneed-profile-form__photo-preview"
                    />
                  ) : (
                    <PhotoIcon />
                  )}
                </Box>

                {preview && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDeletePhotoPopupOpen(true);
                    }}
                    sx={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      backgroundColor: "#fff",
                      boxShadow: 2,
                      zIndex: 2,
                      "&:hover": { backgroundColor: "#f5f5f5" },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Typography className="preneed-profile-form__upload-text">
                {preview ? "Change Photo" : "Upload Photo"}
              </Typography>
            </Box>
          </div>

          <Grid container spacing={2} className="preneed-profile-form__grid">
            <Grid item xs={12} md={6}>
              {renderLabel("Full Name", true)}
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter Full Name"
                size="small"
                name="fullName"
                value={formik.values.fullName}
                onChange={handleAlphaChange}
                onBlur={formik.handleBlur}
                error={formik.touched.fullName && Boolean(formik.errors.fullName)}
                helperText={formik.touched.fullName && (formik.errors.fullName as any)}
                disabled={disableLockedFields.fullName}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Preneed ID", true)}
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter Preneed ID"
                size="small"
                name="preneedId"
                value={formik.values.preneedId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={disableLockedFields.preneedId}
              />
            </Grid>



            <Grid item xs={12} md={6}>
              {renderLabel("Business Email Address", true)}
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter Email Address"
                size="small"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && (formik.errors.email as any)}
                disabled={disableLockedFields.email}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Phone Number", true)}
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter Phone Number"
                size="small"
                name="phoneNumber"
                value={formik.values.phoneNumber}
                onChange={handleDigitChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                helperText={formik.touched.phoneNumber && (formik.errors.phoneNumber as any)}
                disabled={disableLockedFields.phoneNumber}
              />
            </Grid>


          </Grid>
        </div>

        <Divider className="preneed-profile-form__divider" />

        {/* 2. Address (single) */}
        <div className="preneed-profile-form__section">
          <Box className="preneed-profile-form__heading-box">
            <Typography variant="h6" className="preneed-profile-form__heading">
              Address Information
            </Typography>
          </Box>

          {renderLabel("Street/Area")}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Enter Street Address"
            size="small"
            name="street"
            value={formik.values.address.street}
            onChange={handleAddressChange}
            onBlur={formik.handleBlur}
            error={formik.touched.address?.street && Boolean((formik.errors as any)?.address?.street)}
            helperText={formik.touched.address?.street && (formik.errors as any)?.address?.street}
            className="preneed-profile-form__input--full"
          />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderLabel("City")}
              <Autocomplete
                options={cityOptions}
                getOptionLabel={(option) => option.label}
                value={cityByValue.get(formik.values.address.city) || null}
                onChange={handleCityChange}
                onBlur={formik.handleBlur}
                filterOptions={limitFilterOptions}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                openOnFocus
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select City"
                    size="small"
                    variant="outlined"
                    error={formik.touched.address?.city && Boolean((formik.errors as any)?.address?.city)}
                    helperText={formik.touched.address?.city && (formik.errors as any)?.address?.city}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("State")}
              <Autocomplete
                options={stateOptions}
                getOptionLabel={(option) => option.label}
                value={stateByValue.get(formik.values.address.state) || null}
                onChange={handleStateChange}
                onBlur={formik.handleBlur}
                filterOptions={limitFilterOptions}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                openOnFocus
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select State"
                    size="small"
                    variant="outlined"
                    error={formik.touched.address?.state && Boolean((formik.errors as any)?.address?.state)}
                    helperText={formik.touched.address?.state && (formik.errors as any)?.address?.state}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Country")}
              <TextField
                select
                fullWidth
                variant="outlined"
                size="small"
                name="country"
                value={formik.values.address.country}
                onChange={async (e) => {
                  const val = e.target.value;
                  await formik.setFieldValue("address.country", val, true);
                  await formik.setFieldTouched("address.country", true, false);
                  await formik.validateField("address.country");
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.address?.country && Boolean((formik.errors as any)?.address?.country)}
                helperText={formik.touched.address?.country && (formik.errors as any)?.address?.country}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) =>
                    selected ? selected : <span style={{ color: "#9e9e9e" }}>-- Select Country --</span>,
                }}
              >
                <MenuItem value="">
                  -- Select Country --
                </MenuItem>
                <MenuItem value="USA">USA</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              {renderLabel("Zipcode")}
              <Autocomplete
                options={zipOptions}
                getOptionLabel={(option) => option.label}
                value={zipByValue.get(formik.values.address.zipcode) || null}
                onChange={handleZipcodeChange}
                onBlur={formik.handleBlur}
                filterOptions={limitFilterOptions}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                openOnFocus
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Zipcode"
                    size="small"
                    variant="outlined"
                    error={formik.touched.address?.zipcode && Boolean((formik.errors as any)?.address?.zipcode)}
                    helperText={formik.touched.address?.zipcode && (formik.errors as any)?.address?.zipcode}
                  />
                )}
              />
            </Grid>
          </Grid>
        </div>

        {/* Buttons aligned right (same) */}
        <Box className="preneed-profile-form__buttons">
          <Button
            className="preneed-profile-form__cancel"
            variant="outlined"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={uploading}
            className="preneed-profile-form__submit enabled"
          >
            {isProfileCreated ? "Save Changes" : "Save"}
          </Button>
        </Box>
      </form>

      <ActionConfirmationPopup
        open={isDeletePhotoPopupOpen}
        title="Remove Profile Photo"
        note="Are you sure you want to remove your profile photo?"
        onClose={() => setIsDeletePhotoPopupOpen(false)}
        onConfirm={handleDeletePhoto}
        type="delete"
        confirmButtonText="Remove"
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            "& .MuiAlert-message": {
              padding: "8px 0",
            },
            "& .MuiAlert-icon": {
              display: "flex",
              alignItems: "center",
              padding: 0,
            },
            "& .MuiAlert-action": {
              display: "flex",
              alignItems: "center",
              padding: 0,
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PreneedProfileForm;
